import type { SxProps } from '@mui/material';
import Stack from '@mui/material/Stack';

import type { CommonCase } from 'models';

import { CaseLevel1Code } from 'shared/constants/common-cases';
import useTranslation from 'shared/hooks/useTranslation';
import formatDate from 'shared/utils/formatDate';
import safeParseHtml from 'shared/utils/safeParseHtml';

import {
  Card,
  CardContainer,
  CardTitle,
  Item,
  List,
  ListContainer,
  Label,
  ListTitle,
  Value,
} from 'shared/components/styled/Cases';
import CheckIcon from 'shared/icons/Check';
import ClearIcon from 'shared/icons/Clear';

const toFormatedDate = (str: string | null) =>
  str ? formatDate(new Date(str)) : null;

const renderVariantIcon = (flag: boolean) => {
  const sx: SxProps = { fontSize: '1.3rem' };

  return flag ? (
    <CheckIcon color="success" sx={sx} />
  ) : (
    <ClearIcon color="error" sx={sx} />
  );
};

type CaseInfoProps = {
  commonCase: CommonCase;
};

const CaseInfo = ({ commonCase }: CaseInfoProps) => {
  const { t } = useTranslation();

  const showEnteringSection =
    commonCase.CaseLevel1?.Value === CaseLevel1Code.FlawNotification;

  return (
    <CardContainer>
      <CardTitle>{t('Information')}</CardTitle>
      <Card>
        <ListContainer>
          <List>
            <Item>
              <Label>{t('##attribute#incident#createdon')}</Label>
              <Value>{toFormatedDate(commonCase.CreatedOn)}</Value>
            </Item>
            <Item>
              <Label>{t('##attribute#incident#modifiedon')}</Label>
              <Value>{toFormatedDate(commonCase.ModifiedOn)}</Value>
            </Item>
            <Item>
              <Label>{t('##attribute#incident#uds_requesttype')}</Label>
              <Value>
                <Stack alignItems="flex-end" spacing="10px">
                  {commonCase.CaseLevel1 && (
                    <span>{commonCase.CaseLevel1.Label}</span>
                  )}
                  {commonCase.CaseLevel2 && (
                    <span>{commonCase.CaseLevel2.Label}</span>
                  )}
                  {commonCase.CaseLevel3 && (
                    <span>{commonCase.CaseLevel3.Label}</span>
                  )}
                </Stack>
              </Value>
            </Item>
            <Item>
              <Label>{t('##attribute#uds_space#uds_cooperativeid')}</Label>
              <Value>{commonCase.Cooperative?.Name}</Value>
            </Item>
          </List>
        </ListContainer>

        {showEnteringSection && (
          <ListContainer>
            <ListTitle>Ohjeet</ListTitle>
            <List>
              <Item>
                <Label>{t('##attribute#incident#uds_masterkeyallowed')}</Label>
                <Value>
                  {renderVariantIcon(!!commonCase.IsMasterKeyAllowed)}
                </Value>
              </Item>
              <Item>
                <Label>
                  {t('##attribute#incident#uds_appartmenthasadditionallock')}
                </Label>
                <Value>
                  {renderVariantIcon(
                    !!commonCase.IsAppartmentHasAdditionalLock
                  )}
                </Value>
              </Item>
              <Item>
                <Label>
                  {t('##attribute#incident#uds_repairformneedtobeagreed')}
                </Label>
                <Value>
                  {renderVariantIcon(!!commonCase.IsRepairTimeNeedToBeAgreed)}
                </Value>
              </Item>
            </List>
          </ListContainer>
        )}

        <ListContainer>
          <ListTitle>Seuranta</ListTitle>
          <List>
            <Item>
              <Label>{t('##attribute#incident#uds_needsendsms')}</Label>
              <Value>{renderVariantIcon(!!commonCase.SendSms)}</Value>
            </Item>
            <Item>
              <Label>{t('##attribute#incident#uds_needsendemail')}</Label>
              <Value>{renderVariantIcon(!!commonCase.SendEmail)}</Value>
            </Item>
          </List>
        </ListContainer>

        <ListContainer>
          <ListTitle>Kuvaus</ListTitle>
          <List>
            <Value
              sx={{
                justifyContent: 'flex-start',
                textAlign: 'left',
                whiteSpace: 'pre-line',
              }}
            >
              {safeParseHtml(commonCase.Description ?? '')}
            </Value>
          </List>
        </ListContainer>
      </Card>
    </CardContainer>
  );
};

export default CaseInfo;
